import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AdsBanner from '@/components/feature/AdsBanner';
import ApartmentCard from './components/ApartmentCard';
import { apartments, apartmentTypes, apartmentQuartiers } from '@/mocks/apartments';
import type { Apartment } from '@/mocks/apartments';
import { supabase } from '@/lib/supabase';
import { mapDbApartment } from '@/lib/apartments';

function ApartmentsPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [transaction, setTransaction] = useState('all');
  const [type, setType] = useState('all');
  const [quartier, setQuartier] = useState('all');
  const [maxPrice, setMaxPrice] = useState(0);
  const [sortBy, setSortBy] = useState('recent');
  const [favorites, setFavorites] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(true);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [dbApartments, setDbApartments] = useState<Apartment[]>([]);

  useEffect(() => {
    const stored = localStorage.getItem('wa_apartment_favorites');
    if (stored) {
      try {
        setFavorites(JSON.parse(stored));
      } catch {
        setFavorites([]);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('wa_apartment_favorites', JSON.stringify(favorites));
  }, [favorites]);

  useEffect(() => {
    let mounted = true;
    supabase
      .from('apartments')
      .select('*')
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        if (mounted && data) {
          setDbApartments(data.map(mapDbApartment));
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  const toggleFavorite = (id: string) => {
    setFavorites((prev) =>
      prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]
    );
  };

  const filtered = useMemo(() => {
    const all = [...dbApartments, ...apartments];
    let result = all.filter((a) => {
      const matchesQuery =
        searchQuery.trim() === '' ||
        a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.quartier.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.city.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesTransaction = transaction === 'all' || a.transaction === transaction;
      const matchesType = type === 'all' || a.type === type;
      const matchesQuartier = quartier === 'all' || a.quartier === quartier;
      const matchesPrice = maxPrice === 0 || a.price <= maxPrice;
      const matchesFavorite = !favoritesOnly || favorites.includes(a.id);

      return matchesQuery && matchesTransaction && matchesType && matchesQuartier && matchesPrice && matchesFavorite;
    });

    result = [...result].sort((a, b) => {
      if (sortBy === 'price-asc') return a.price - b.price;
      if (sortBy === 'price-desc') return b.price - a.price;
      if (sortBy === 'surface') return b.surface - a.surface;
      return new Date(b.postedAt).getTime() - new Date(a.postedAt).getTime();
    });

    return result;
  }, [dbApartments, searchQuery, transaction, type, quartier, maxPrice, sortBy, favoritesOnly, favorites]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-24 md:pt-32 pb-12 md:pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20African%20city%20skyline%20at%20dusk%20with%20residential%20buildings%20and%20warm%20lights%2C%20real%20estate%20concept%2C%20deep%20blue%20and%20teal%20tones%2C%20cinematic%20photography%2C%20no%20text&width=1600&height=500&seq=apartments-hero&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-50/90 via-background-50/70 to-background-50" />
        </div>

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block text-sm font-semibold text-primary-400 uppercase tracking-wider mb-3">
              WorldApeiron Apartments
            </span>
            <h1 className="font-heading font-bold text-3xl md:text-5xl text-foreground-50 mb-4 leading-tight">
              Trouvez le bien qui vous ressemble
            </h1>
            <p className="text-base md:text-lg text-foreground-400 max-w-2xl mx-auto mb-8">
              Appartements, villas, terrains et bureaux à louer ou à vendre partout au Togo.
            </p>

            {/* Search */}
            <form
              onSubmit={(e) => e.preventDefault()}
              className="max-w-xl mx-auto mb-6"
            >
              <div className="flex items-center bg-background-100/80 backdrop-blur-md border border-background-300/60 rounded-lg overflow-hidden">
                <div className="w-12 h-12 flex items-center justify-center shrink-0">
                  <i className="ri-search-line text-lg text-foreground-500" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher par ville, quartier ou type de bien..."
                  className="flex-1 bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 py-3 outline-none"
                />
              </div>
            </form>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => navigate('/dashboard/apartments/new')}
                className="px-6 py-3 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors whitespace-nowrap text-sm"
              >
                Publier une annonce
              </button>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="px-6 py-3 border border-foreground-400/50 text-foreground-50 font-semibold rounded-lg hover:bg-foreground-50/10 transition-colors whitespace-nowrap text-sm flex items-center gap-2"
              >
                <i className="ri-equalizer-line" />
                Filtres
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 pt-8">
        <AdsBanner pageKey="apartments" />
      </section>

      {/* Filters */}
      {showFilters && (
      <section className="w-full px-4 md:px-8 lg:px-12 pb-6">
        <div className="bg-background-100 border border-background-200/70 rounded-xl p-4 md:p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {/* Transaction */}
            <div>
              <label className="block text-xs font-semibold text-foreground-500 mb-1.5">Transaction</label>
              <div className="flex rounded-md overflow-hidden border border-background-300/60">
                {[
                  { value: 'all', label: 'Tous' },
                  { value: 'location', label: 'Louer' },
                  { value: 'vente', label: 'Acheter' },
                ].map((t) => (
                  <button
                    key={t.value}
                    onClick={() => setTransaction(t.value)}
                    className={`flex-1 px-3 py-2 text-xs font-medium whitespace-nowrap transition-colors ${
                      transaction === t.value
                        ? 'bg-primary-500 text-foreground-950'
                        : 'text-foreground-500 hover:text-foreground-50'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Type */}
            <div>
              <label className="block text-xs font-semibold text-foreground-500 mb-1.5">Type de bien</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full bg-background-50 border border-background-300/60 rounded-md px-3 py-2 text-sm text-foreground-50 outline-none focus:border-primary-400"
              >
                <option value="all">Tous les types</option>
                {apartmentTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            {/* Quartier */}
            <div>
              <label className="block text-xs font-semibold text-foreground-500 mb-1.5">Quartier</label>
              <select
                value={quartier}
                onChange={(e) => setQuartier(e.target.value)}
                className="w-full bg-background-50 border border-background-300/60 rounded-md px-3 py-2 text-sm text-foreground-50 outline-none focus:border-primary-400"
              >
                <option value="all">Tous les quartiers</option>
                {apartmentQuartiers.map((q) => (
                  <option key={q} value={q}>{q}</option>
                ))}
              </select>
            </div>

            {/* Price */}
            <div>
              <label className="block text-xs font-semibold text-foreground-500 mb-1.5">
                Prix max (FCFA)
              </label>
              <select
                value={maxPrice}
                onChange={(e) => setMaxPrice(Number(e.target.value))}
                className="w-full bg-background-50 border border-background-300/60 rounded-md px-3 py-2 text-sm text-foreground-50 outline-none focus:border-primary-400"
              >
                <option value={0}>Sans limite</option>
                <option value={200000}>200 000</option>
                <option value={350000}>350 000</option>
                <option value={500000}>500 000</option>
                <option value={50000000}>50 000 000</option>
                <option value={100000000}>100 000 000</option>
                <option value={250000000}>250 000 000</option>
              </select>
            </div>

            {/* Sort */}
            <div>
              <label className="block text-xs font-semibold text-foreground-500 mb-1.5">Trier par</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full bg-background-50 border border-background-300/60 rounded-md px-3 py-2 text-sm text-foreground-50 outline-none focus:border-primary-400"
              >
                <option value="recent">Plus récent</option>
                <option value="price-asc">Prix croissant</option>
                <option value="price-desc">Prix décroissant</option>
                <option value="surface">Surface</option>
              </select>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Results */}
      <section className="w-full px-4 md:px-8 lg:px-12 pb-20">
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-foreground-400">
            <span className="font-semibold text-foreground-50">{filtered.length}</span> bien{filtered.length > 1 ? 's' : ''} trouvé{filtered.length > 1 ? 's' : ''}
          </p>
          {favorites.length > 0 && (
            <button
              onClick={() => setFavoritesOnly(!favoritesOnly)}
              className={`text-sm transition-colors flex items-center gap-1.5 ${
                favoritesOnly ? 'text-primary-400 font-semibold' : 'text-primary-400 hover:text-primary-300'
              }`}
            >
              <i className="ri-heart-3-line" />
              {favorites.length} favori{favorites.length > 1 ? 's' : ''}
            </button>
          )}
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-search-line text-2xl text-foreground-400" />
            </div>
            <p className="text-foreground-500 font-medium mb-2">Aucun bien ne correspond à vos critères</p>
            <p className="text-sm text-foreground-500">Essayez d'élargir votre recherche ou de réinitialiser les filtres.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {filtered.map((apartment) => (
              <ApartmentCard
                key={apartment.id}
                apartment={apartment}
                isFavorite={favorites.includes(apartment.id)}
                onToggleFavorite={toggleFavorite}
              />
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}

export default ApartmentsPage;